import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FileText, Link2, Upload, Database, Lightbulb, ClipboardList } from "lucide-react";
import { useDocHubTags } from "@/hooks/useDocHubTags";
import { useDocHubSnippetsTags } from "@/hooks/useDocHubSnippetsTags";

export type SourceType = 'url' | 'text' | 'file' | 'dochub';

export interface SelectedDocHubItems {
  tips: string[];
  files: string[];
  moms: string[];
}

interface Tip {
  id: string;
  name: string;
  content: string;
  tags?: string[];
}

interface DocHubFile {
  id: string;
  name: string;
  file_type: string;
  link?: string;
  tags?: string[];
}

interface Mom {
  id: string;
  filename: string;
  summary?: string;
  transcript_content: string;
  status: string;
  created_at: string;
}

interface Brand {
  id: string;
  name: string;
}

interface ContentSourceSelectorProps {
  sourceType: SourceType;
  onSourceTypeChange: (type: SourceType) => void;
  
  // Text input
  textInput: string;
  onTextInputChange: (value: string) => void;
  textPlaceholder?: string;
  textLabel?: string;
  
  // URL input
  urlInput: string;
  onUrlInputChange: (value: string) => void;
  urlPlaceholder?: string;
  urlLabel?: string;
  urlHelpText?: string;
  
  // File input (browser File object)
  fileInput: globalThis.File | null;
  onFileInputChange: (file: globalThis.File | null) => void;
  fileAccept?: string;
  fileLabel?: string;
  fileHelpText?: string;
  
  // Doc Hub
  selectedDocHubItems?: SelectedDocHubItems;
  onToggleTip?: (tipId: string) => void;
  onToggleFile?: (fileId: string) => void;
  onToggleMom?: (momId: string) => void;
  currentBrand?: Brand | null;
  tips?: Tip[];
  files?: DocHubFile[];
  moms?: Mom[];
  brandId?: string;
  
  // Customization
  showDocHub?: boolean;
  className?: string;
}

export function ContentSourceSelector({
  sourceType,
  onSourceTypeChange,
  textInput,
  onTextInputChange,
  textPlaceholder = "Paste your content here...",
  textLabel = "Content",
  urlInput,
  onUrlInputChange,
  urlPlaceholder = "https://example.com/article",
  urlLabel = "Website URL",
  urlHelpText = "We'll extract content from this URL",
  fileInput,
  onFileInputChange,
  fileAccept = ".pdf,.doc,.docx,.txt",
  fileLabel = "Upload File",
  fileHelpText = "Supported formats: PDF, DOC, DOCX, TXT",
  selectedDocHubItems,
  onToggleTip,
  onToggleFile,
  onToggleMom,
  currentBrand,
  tips,
  files,
  moms,
  brandId,
  showDocHub = true,
  className,
}: ContentSourceSelectorProps) {
  const { tags: fileTags } = useDocHubTags(brandId || '');
  const { tags: snippetTags } = useDocHubSnippetsTags(brandId || '');

  const getTagName = (tagId: string, isSnippet: boolean = false): string | null => {
    const tagList = isSnippet ? snippetTags : fileTags;
    const tag = tagList.find(t => t.id === tagId);
    return tag?.name || null;
  };

  const parseTags = (tags: any): string[] => {
    if (!tags) return [];
    if (Array.isArray(tags)) return tags.map(String);
    if (typeof tags === 'string') {
      try {
        const parsed = JSON.parse(tags);
        return Array.isArray(parsed) ? parsed.map(String) : [];
      } catch {
        return [];
      }
    }
    return [];
  };
  return (
    <Tabs value={sourceType} onValueChange={(v) => onSourceTypeChange(v as SourceType)} className={className}>
      <TabsList className={`grid w-full ${showDocHub ? 'grid-cols-4' : 'grid-cols-3'}`}>
        <TabsTrigger value="text">
          <FileText className="h-4 w-4 mr-2" />
          Text
        </TabsTrigger>
        <TabsTrigger value="url">
          <Link2 className="h-4 w-4 mr-2" />
          URL
        </TabsTrigger>
        <TabsTrigger value="file">
          <Upload className="h-4 w-4 mr-2" />
          File
        </TabsTrigger>
        {showDocHub && (
          <TabsTrigger value="dochub">
            <Database className="h-4 w-4 mr-2" />
            Doc Hub
          </TabsTrigger>
        )}
      </TabsList>

      <TabsContent value="text" className="space-y-4">
        <div>
          <Label htmlFor="text-input">{textLabel}</Label>
          <Textarea
            id="text-input"
            placeholder={textPlaceholder}
            className="min-h-[200px]"
            value={textInput}
            onChange={(e) => onTextInputChange(e.target.value)}
          />
        </div>
      </TabsContent>

      <TabsContent value="url" className="space-y-4">
        <div>
          <Label htmlFor="url-input">{urlLabel}</Label>
          <Input
            id="url-input"
            type="url"
            placeholder={urlPlaceholder}
            value={urlInput}
            onChange={(e) => onUrlInputChange(e.target.value)}
          />
          {urlHelpText && (
            <p className="text-sm text-muted-foreground mt-2">{urlHelpText}</p>
          )}
        </div>
      </TabsContent>

      <TabsContent value="file" className="space-y-4">
        <div>
          <Label htmlFor="file-input">{fileLabel}</Label>
          <Input
            id="file-input"
            type="file"
            accept={fileAccept}
            onChange={(e) => onFileInputChange(e.target.files?.[0] || null)}
          />
          {fileHelpText && (
            <p className="text-sm text-muted-foreground mt-2">{fileHelpText}</p>
          )}
        </div>
      </TabsContent>

      {showDocHub && (
        <TabsContent value="dochub" className="space-y-2">
          {!currentBrand ? (
            <Alert>
              <AlertDescription>Please select a brand to view Doc Hub content</AlertDescription>
            </Alert>
          ) : (
            <ScrollArea className="h-[280px] border rounded-md p-2">
              {(() => {
                // Combine all items into a single list with type info
                const allItems: Array<{
                  id: string;
                  type: 'tip' | 'file' | 'mom';
                  name: string;
                  subtitle: string;
                  tags?: string[];
                  badge?: string;
                  isSelected: boolean;
                  onToggle: () => void;
                }> = [];

                // Add tips
                if (tips && onToggleTip && selectedDocHubItems) {
                  tips.forEach(tip => {
                    allItems.push({
                      id: tip.id,
                      type: 'tip',
                      name: tip.name,
                      subtitle: tip.content.substring(0, 50) + '...',
                      tags: parseTags(tip.tags),
                      isSelected: selectedDocHubItems.tips.includes(tip.id),
                      onToggle: () => onToggleTip(tip.id),
                    });
                  });
                }

                // Add files
                if (files && onToggleFile && selectedDocHubItems) {
                  files.forEach(file => {
                    allItems.push({
                      id: file.id,
                      type: 'file',
                      name: file.name,
                      subtitle: file.file_type,
                      tags: parseTags(file.tags),
                      badge: file.file_type,
                      isSelected: selectedDocHubItems.files.includes(file.id),
                      onToggle: () => onToggleFile(file.id),
                    });
                  });
                }

                // Add MOMs
                if (moms && onToggleMom && selectedDocHubItems) {
                  moms.forEach(mom => {
                    allItems.push({
                      id: mom.id,
                      type: 'mom',
                      name: mom.filename,
                      subtitle: mom.summary?.substring(0, 50) || mom.transcript_content.substring(0, 50) + '...',
                      badge: mom.status,
                      isSelected: selectedDocHubItems.moms.includes(mom.id),
                      onToggle: () => onToggleMom(mom.id),
                    });
                  });
                }

                if (allItems.length === 0) {
                  return (
                    <p className="text-muted-foreground text-center py-8 text-sm">
                      No Doc Hub content available
                    </p>
                  );
                }

                const getTypeIcon = (type: 'tip' | 'file' | 'mom') => {
                  switch (type) {
                    case 'tip':
                      return <Lightbulb className="h-4 w-4 text-primary shrink-0" />;
                    case 'file':
                      return <ClipboardList className="h-4 w-4 text-primary shrink-0" />;
                    case 'mom':
                      return <FileText className="h-4 w-4 text-primary shrink-0" />;
                  }
                };

                return (
                  <div className="space-y-1">
                    {allItems.map(item => (
                      <div
                        key={`${item.type}-${item.id}`}
                        className="flex items-center gap-2 p-2 rounded-md hover:bg-accent cursor-pointer"
                        onClick={item.onToggle}
                      >
                        <Checkbox 
                          checked={item.isSelected}
                          onCheckedChange={item.onToggle}
                        />
                        {getTypeIcon(item.type)}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
                        </div>
                        {item.badge && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">
                            {item.badge}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </ScrollArea>
          )}
        </TabsContent>
      )}
    </Tabs>
  );
}
